/**
 * The Kalendarz tab's wiring: the connection's read, the trip to Google and the disconnect,
 * sequenced by `model/googleCalendarFlow` (calendar-sync-requirements R10).
 *
 * The read is made whichever tab is open — its answer decides whether the strip offers Kalendarz.
 * The trip to Google ends this document: `authorize-url` mints a consent address and a `state`,
 * the state is stashed, and the page is replaced. `/konto/google` finishes it and comes back with
 * `?google=`, which is read once here, said, and dropped from the address.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deleteMeCalendarConnectionsGoogle } from '~/shared/api/generated/endpoints/my-google-calendar-connection/my-google-calendar-connection';
import type { TrainWithMeModulesTrainingDtosResponsesGoogleCalendarConnectionResponse as GoogleConnectionDto } from '~/shared/api/generated/model';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { tabButtonId } from '~/shared/ui/TabStrip';
import {
  goToGoogleConsent,
  googleCalendarQueryKey,
  googleCalendarQueryOptions,
} from '~/entities/google-calendar-connection';

import { DEFAULT_ACCOUNT_TAB, accountTabSearch } from '../model/accountTabs';
import {
  calendarTabShown,
  firstFillRefetchInterval,
  googleCalendarView,
  type GoogleCalendarView,
} from '../model/googleCalendar';
import {
  googleCalendarFlow,
  googleCalendarFlowAfterReturn,
  type GoogleCalendarFace,
  type GoogleCalendarFlow,
  type GoogleCalendarFlowEvent,
} from '../model/googleCalendarFlow';
import type { SectionState } from './useSectionRead';

export interface GoogleCalendarApi {
  /** Whether the strip offers Kalendarz at all. */
  readonly shown: boolean;

  /**
   * What the connection's read says. The tab's skeleton gate is NOT here: the Integracje rows wait
   * for this read, the Apple feed and the standing link together, and only `CalendarTab` holds all
   * three (the other two hooks are called after the strip has decided, from this read, which tab
   * stands).
   */
  readonly state: SectionState;
  readonly onRetry: () => void;

  /** `null` until the read has answered. */
  readonly view: GoogleCalendarView | null;
  /** When the read last answered — the clock the last-sync line is worded against. */
  readonly answeredAt: number;
  readonly flow: GoogleCalendarFlow;
  readonly onConnect: () => void;
  readonly onAskDisconnect: () => void;
  readonly onCancelConfirm: () => void;
  readonly onConfirmDisconnect: () => void;
  readonly onFocusTaken: () => void;

  readonly announcement: string;
}

/** @param active whether Kalendarz is the tab the address asks for. */
export function useGoogleCalendar(active: boolean): GoogleCalendarApi {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_shell/konto' });
  const { message: announcement, announce } = useAnnouncer();

  const [firstFill] = useState(() => {
    const interval = firstFillRefetchInterval();
    return (query: { state: { data: GoogleConnectionDto | undefined } }) => interval(query.state.data);
  });
  const read = useQuery({ ...googleCalendarQueryOptions(), refetchInterval: active ? firstFill : false });
  const view = read.data === undefined ? null : googleCalendarView(read.data);
  const state: SectionState = read.isError ? 'error' : active && read.isPending ? 'pending' : 'ready';
  const face: GoogleCalendarFace =
    state === 'ready' && view !== null && view.kind !== 'unavailable' ? view.kind : 'none';

  // The return page's outcome is the machine's first state, so its banner stands from the first frame.
  const [returned] = useState(() => search.google ?? null);
  const [flow, setFlow] = useState<GoogleCalendarFlow>(() => googleCalendarFlowAfterReturn(returned));
  const step = (event: GoogleCalendarFlowEvent) => {
    setFlow((current) => googleCalendarFlow(current, event));
  };

  /* Said after the region has mounted empty, and dropped from the address: a reload must not
   * say it again. Replaces, so Back does not land on it. */
  const saidOnce = useRef(false);
  useEffect(() => {
    if (saidOnce.current || returned === null) return;
    saidOnce.current = true;
    if (returned === 'connected') announce(t('web:account.calendar.connected'));
    void navigate({ to: '.', search: () => accountTabSearch('integracje'), replace: true });
  }, [returned, announce, navigate, t]);

  // Leaving the tab, or the read changing under it, is not a press — adjusted during render.
  const seen = `${String(active)}|${face}`;
  const [lastSeen, setLastSeen] = useState(seen);
  if (seen !== lastSeen) {
    setLastSeen(seen);
    if (!active) step({ type: 'DEACTIVATED' });
    else {
      if (lastSeen.startsWith('false')) step({ type: 'ACTIVATED' });
      step({ type: 'FACE', face });
    }
  }

  /* Back from Google's page restores this document from the back-forward cache with the redirect
   * still pending, and fires no focus event — the button is freed and the read asked again here. */
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setFlow((current) => googleCalendarFlow(current, { type: 'RESTORED' }));
      void queryClient.invalidateQueries({ queryKey: googleCalendarQueryKey });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [queryClient]);

  const disconnect = useMutation<GoogleConnectionDto>({
    mutationFn: () => deleteMeCalendarConnectionsGoogle(),
    onSuccess: (next) => {
      queryClient.setQueryData(googleCalendarQueryKey, next);
      step({ type: 'DISCONNECTED', connected: next.connected });
      if (!next.connected) announce(t('web:account.calendar.disconnected'));
    },
    onError: async () => {
      // R9: the row is marked disconnecting first, so a lost answer may still have disconnected.
      const reread = await queryClient
        .fetchQuery({ ...googleCalendarQueryOptions(), staleTime: 0 })
        .then(googleCalendarView, () => null);
      if (reread?.kind !== 'notConnected') {
        step({ type: 'DISCONNECT_FAILED' });
        return;
      }
      step({ type: 'DISCONNECTED', connected: false });
      announce(t('web:account.calendar.disconnected'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: googleCalendarQueryKey });
    },
  });

  /* The page leaves for Google only from a mounted screen still connecting: an unmount aborts the
   * read, and a press undone in the meantime (a page restored from the back cache) keeps it here. */
  const flowNow = useRef(flow);
  useEffect(() => {
    flowNow.current = flow;
  });
  const authorizing = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      authorizing.current?.abort();
    },
    [],
  );

  const onConnect = () => {
    const next = googleCalendarFlow(flow, { type: 'CONNECT', face });
    if (next === flow) return;
    setFlow(next);
    flowNow.current = next;
    const controller = new AbortController();
    authorizing.current = controller;
    goToGoogleConsent(controller.signal, () => flowNow.current.busy === 'connect').catch(() => {
      if (controller.signal.aborted) return;
      step({ type: 'CONNECT_FAILED' });
      // A 409 says Google is not offered here any more; the read takes the tab away.
      void queryClient.invalidateQueries({ queryKey: googleCalendarQueryKey });
    });
  };

  /* The read took the open tab away (a 409 under the press): its panel unmounted with focus in it,
   * so focus lands on the tab shown instead, and the reason is said. */
  const shown = calendarTabShown(view, active);
  const offered = view !== null && view.kind !== 'unavailable';
  const wasOffered = useRef(offered);
  useEffect(() => {
    // Only a tab that stood: a pasted address in an environment without Google lands on Profil silently.
    const taken = wasOffered.current && !shown && active;
    if (view !== null) wasOffered.current = offered;
    if (!taken) return;
    announce(t('web:account.calendar.unavailable'));
    if (document.activeElement === null || document.activeElement === document.body) {
      document.getElementById(tabButtonId(DEFAULT_ACCOUNT_TAB))?.focus();
    }
  }, [view, offered, shown, active, announce, t]);

  return {
    shown,
    state,
    onRetry: () => {
      void read.refetch();
    },

    view,
    answeredAt: read.dataUpdatedAt,
    flow,
    onConnect,
    onAskDisconnect: () => {
      step({ type: 'ASK_DISCONNECT', face });
    },
    onCancelConfirm: () => {
      step({ type: 'CANCEL' });
    },
    onConfirmDisconnect: () => {
      const next = googleCalendarFlow(flow, { type: 'CONFIRM' });
      if (next === flow) return;
      setFlow(next);
      disconnect.mutate();
    },
    onFocusTaken: () => {
      step({ type: 'FOCUS_TAKEN' });
    },

    announcement,
  };
}
